import {
  Box,
  Typography,
  InputLabel,
  MenuItem,
  Select,
  Card,
  FormControl,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import Loading from "../../../../components/loading/loading";
import Notification from "../../../../components/notification/notification";
import InputNumber from "../../../../components/input-number/input-number";
import ProjectSection from "../../add-order/project-section/project-section";
import { useDispatch, useSelector } from "react-redux";
import {
  getClientAllUsersRequest,
  getClientsListRequest,
  getClientSubDivisionListRequest,
  getFormDetailsRequest,
  getFormListByClientIdRequest,
  getProcessesProcedureRequest,
} from "../../../../redux/clients/slice";
import { resetPropertyData } from "../../../../redux/properties/slice";
import {
  getAdjustmentTypesRequest,
  getProjectsRequest,
} from "../../../../redux/orders/slice";
import Fees from "./fees/fees";
import AdditionalInformation from "./additional-information/additional-information";
import { getOrderDetailsRequest } from "../../../../redux/orders/slice";
import { updateOrderRequest } from "../../../../utils/APICalls";
import "../../add-order/add-order.css";
import EditPropertyDetails from "./edit-property-details/edit-property-details";
import PropertySection from "../../add-order/property-section/property-section";
import {
  pruneEmptyFields,
  propertyContactPhoneTypeForApi,
  propertyContactEmailForApi,
  mapPropertyContactsForFormValues,
} from "../../../../utils/Formatter";
import Contacts from "./contacts/contacts";
import Attachments from "./attachments/attachments";
import OptionalOrderDetails from "../../add-order/optional-order-details/optional-order-details";
import { propertyDetailsTypes } from "../../../../utils/Constant";

interface ExternalContact {
  fullName: string;
  email: string;
}

type FormValues = {
  clientId: string;
  subDivision: string;
  project: string;
  requesterId: string;
  isContactInspection: boolean;
  inspectionScheduleType: string;
  photoCount: number;
  option1: string;
  option2: string;
  option3: string;
  propertyId: string;
  propertyRecord: any;
  propertyContacts: any[];
  formId: string;
  startDateToday: boolean;
  startDate: string;
  appointmentDate: string | null;
  inspectorDueDate: string | null;
  clientDueDate: string;
  isRushInspection: boolean;
  rushFeeLabel: string;
  contacts: {
    showVisionUsers: boolean;
    showExtraContacts: boolean;
    includeRequester: boolean;
    contactIds: string[];
    externalContacts: ExternalContact[];
  };
  fees: any[];
  notes: any[];
  attachments: any[];
  newAttachments: any[];
};

interface EditOrderDetailsProps {
  orderId: string;
  shouldSaveForm: boolean;
  setShouldSaveForm: () => void;
  onSaveComplete: () => void;
  orderStatus: number;
  feesSectionRef: React.RefObject<HTMLDivElement>;
  changePropertyMode?: boolean;
  onExitChangeProperty?: () => void;
}

export default function EditOrderDetails({
  orderId,
  shouldSaveForm,
  setShouldSaveForm,
  onSaveComplete,
  orderStatus,
  feesSectionRef,
  changePropertyMode = false,
  onExitChangeProperty,
}: EditOrderDetailsProps) {
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const {
    loading,
    clientsList,
    subDivisionList,
    clientAllUsers,
    formsList,
    processesProcedure,
    formDetails,
  } = useSelector((state: any) => state.clients);

  const { propertyDetails } = useSelector((state: any) => state.property);

  const {
    projects,
    loading: projectsLoading,
    adjustmentTypes,
    orderDetails,
  } = useSelector((state: any) => state.orders);

  const dispatch = useDispatch();
  const handleFormSubmit = async () => {
    try {
      setShouldSaveForm();

      formik.setTouched({
        clientId: true,
        requesterId: true,
        formId: true,
        startDate: true,
        clientDueDate: true,
        appointmentDate: true,
        inspectorDueDate: true,
        rushFeeLabel: true,
        photoCount: true,
        contacts: {
          showVisionUsers: true,
          showExtraContacts: true,
          includeRequester: true,
          contactIds: true,
          externalContacts: true,
          ...(formik.values.contacts?.externalContacts?.length > 0 && {
            externalContacts: formik.values.contacts.externalContacts.map(
              (_: any, index: number) => ({
                fullName: true,
                email: true,
              })
            ),
          }),
        },
        propertyRecord: {
          loanNumber: true,
          propertyName: true,
          street: true,
          city: true,
          state: true,
          zip: true,
          country: true,
          ...(formik.values.propertyRecord?.optionalPropertyDetails?.length >
            0 && {
            optionalPropertyDetails:
              formik.values.propertyRecord?.optionalPropertyDetails.map(
                (_: any, index: number) => ({
                  type: true,
                  description: true,
                })
              ),
          }),
        },
        ...(formik.values.propertyContacts?.length > 0 && {
          propertyContacts: formik.values.propertyContacts.map(
            (_: any, index: number) => ({
              name: true,
              email: true,
              phone: true,
              phoneType: true,
              isPrimary: true,
            })
          ),
        }),
      } as any);

      const errors = await formik.validateForm();

      if (Object.keys(errors).length > 0) {
        return;
      }

      await formik.submitForm();
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  const validationSchema = Yup.object({
    clientId: Yup.string().required("This field can't be empty"),
    requesterId: Yup.string().required("This field can't be empty"),
    formId: Yup.string().required("This field can't be empty"),
    startDate: Yup.string().required("This field can't be empty"),
    appointmentDate: Yup.string()
      .nullable()
      .optional()
      .test(
        "date-validation",
        "Appointment date cannot be before start date",
        function (value) {
          const { startDate } = this.parent;
          if (!value || !startDate) return true;
          return new Date(value) >= new Date(startDate);
        }
      ),
    inspectorDueDate: Yup.string()
      .nullable()
      .optional()
      .test(
        "date-validation",
        "Inspector due date cannot be before start date",
        function (value) {
          const { startDate } = this.parent;
          if (!value || !startDate) return true;
          return new Date(value) >= new Date(startDate);
        }
      ),
    clientDueDate: Yup.string()
      .nullable()
      .required("This field can't be empty")
      .test(
        "date-validation",
        "Client due date cannot be before start date",
        function (value) {
          const { startDate } = this.parent;
          if (!value || !startDate) return true;
          return new Date(value) >= new Date(startDate);
        }
      ),
    rushFeeLabel: Yup.string().when("isRushInspection", {
      is: true,
      then: (schema) => schema.required("This field can't be empty"),
      otherwise: (schema) => schema.optional(),
    }),
    photoCount: Yup.number()
      .typeError("Photo count must be a number")
      .required("This field can't be empty"),
    option1: Yup.string(),
    option2: Yup.string(),
    option3: Yup.string(),
    contacts: Yup.object({
      showVisionUsers: Yup.boolean(),
      showExtraContacts: Yup.boolean(),
      includeRequester: Yup.boolean(),
      contactIds: Yup.array().when("showVisionUsers", {
        is: true,
        then: (schema) => schema.min(1, "This field can't be empty"),
        otherwise: (schema) => schema.optional(),
      }),
      externalContacts: Yup.array().when("showExtraContacts", {
        is: true,
        then: (schema) =>
          schema
            .of(
              Yup.object({
                fullName: Yup.string().required("This field can't be empty"),
                email: Yup.string()
                  .email("Invalid email format")
                  .required("This field can't be empty")
                  .test(
                    "unique-email",
                    "Email address must be unique",
                    function (value) {
                      if (!value) return true;
                      const { path } = this;
                      const contacts =
                        this.from?.[1]?.value?.externalContacts || [];
                      const emails = contacts
                        .map((c: any) => c.email)
                        .filter((email: string) => email);
                      const currentIndex = parseInt(
                        path.split("[")[1]?.split("]")[0] || "0"
                      );
                      const duplicateCount = emails.filter(
                        (email: string, index: number) =>
                          email.toLowerCase() === value.toLowerCase() &&
                          index !== currentIndex
                      ).length;
                      return duplicateCount === 0;
                    }
                  ),
              })
            )
            .min(1, "This field can't be empty"),
        otherwise: (schema) => schema.optional(),
      }),
    }),
    fees: Yup.array(),
    notes: Yup.array(),
    attachments: Yup.array(),
    newAttachments: Yup.array(),
    propertyRecord: Yup.object({
      loanNumber: Yup.string().required("This field can't be empty"),
      street: Yup.string().required("This field can't be empty"),
      city: Yup.string().required("This field can't be empty"),
      state: Yup.string().required("This field can't be empty"),
      zip: Yup.string().required("This field can't be empty"),
      country: Yup.string().required("This field can't be empty"),
      // accountNumber: Yup.string(),
      // referenceNumber: Yup.string(),
      propertyName: Yup.string(),
      // collateralOrPropertyType: Yup.string(),
      // collateralOrPropertyDescription: Yup.string(),
      // yearBuilt: Yup.number()
      //   .typeError("Invalid")
      //   .min(1800, "Year must be after 1800")
      //   .max(new Date().getFullYear(), "Year cannot be in the future"),
      // yearRenovated: Yup.number()
      //   .typeError("Invalid")
      //   .min(1800, "Year must be after 1800")
      //   .max(new Date().getFullYear(), "Year cannot be in the future"),
      // nameOfBusiness: Yup.string(),
      // businessPhone: Yup.string(),
      address2: Yup.string(),
      // numberOfUnits: Yup.number()
      //   .typeError("Invalid")
      //   .min(1, "Must be at least 1"),
      // numberOfBuildings: Yup.number()
      //   .typeError("Invalid")
      //   .min(1, "Must be at least 1"),
      // investorNumber: Yup.string(),
      // squareFootage: Yup.number()
      //   .typeError("Invalid")
      //   .min(1, "Must be at least 1"),
      // operationsAndMaintenanceRequired: Yup.boolean(),
      // loanBalance: Yup.number()
      //   .typeError("Invalid")
      //   .min(0, "Must be 0 or greater"),
      // loanBalanceAsOf: Yup.string(),
      optionalPropertyDetails: Yup.array().of(
        Yup.object({
          type: Yup.string().required("This field can't be empty"),
          description: Yup.string().required("This field can't be empty"),
        })
      ),
    }),
    propertyContacts: Yup.array()
      .of(
        Yup.object({
          name: Yup.string().required("This field can't be empty"),
          email: Yup.string().nullable().email("Invalid email format"),
          phone: Yup.string().required("This field can't be empty"),
          phoneType: Yup.string(),
          isPrimary: Yup.boolean().optional(),
        })
      )
      .min(1, "At least one contact is required"),
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      clientId: "",
      subDivision: "",
      project: "",
      requesterId: "",
      isContactInspection: true,
      inspectionScheduleType: "Appointment",
      photoCount: 0,
      option1: "",
      option2: "",
      option3: "",
      propertyId: "",
      propertyRecord: {
        loanNumber: "",
        // accountNumber: "",
        // referenceNumber: "",
        propertyName: "",
        // collateralOrPropertyType: "",
        // collateralOrPropertyDescription: "",
        // yearBuilt: "",
        // yearRenovated: "",
        // nameOfBusiness: "",
        // businessPhone: "",
        country: "",
        street: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
        // numberOfUnits: "",
        // numberOfBuildings: "",
        // investorNumber: "",
        // squareFootage: "",
        // operationsAndMaintenanceRequired: false,
        // loanBalance: "",
        // loanBalanceAsOf: "",
        optionalPropertyDetails: [],
      },
      propertyContacts: [],
      formId: "",
      startDateToday: false,
      startDate: "",
      appointmentDate: "",
      inspectorDueDate: "",
      clientDueDate: "",
      isRushInspection: false,
      rushFeeLabel: "",
      contacts: {
        showVisionUsers: false,
        showExtraContacts: false,
        includeRequester: false,
        contactIds: [],
        externalContacts: [{ fullName: "", email: "" }],
      },
      fees: [],
      notes: [],
      attachments: [],
      newAttachments: [],
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setShowLoading(true);

        let formIdValue = null;
        if (orderStatus <= 34) {
          if (values.formId !== orderDetails?.form?.id) {
            formIdValue = values.formId;
          }
        }

        const { optionalPropertyDetails, ...rest } = values.propertyRecord;
        const finalPropertyRecord = { ...rest };
        propertyDetailsTypes?.forEach((types) => {
          optionalPropertyDetails?.forEach((details: any) => {
            if (details?.type === types?.value) {
              if (
                types?.value === "yearBuilt" ||
                types?.value === "yearRenovated" ||
                types?.value === "numberOfUnits" ||
                types?.value === "numberOfBuildings" ||
                types?.value === "squareFootage"
              ) {
                finalPropertyRecord[types?.value] = parseInt(
                  details?.description
                );
              } else if (types?.value === "loanBalance") {
                finalPropertyRecord[types?.value] = parseFloat(
                  details?.description
                );
              } else if (types?.value === "operationsAndMaintenanceRequired") {
                finalPropertyRecord[types?.value] =
                  details?.description === "true" ? true : false;
              } else {
                finalPropertyRecord[types?.value] = details?.description;
              }
            }
          });
        });

        const propertyContactsPayload = (values.propertyContacts || []).map(
          (c: any) => ({
            ...c,
            email: propertyContactEmailForApi(c.email),
            phoneType: propertyContactPhoneTypeForApi(c.phoneType),
          })
        );

        const payload = pruneEmptyFields({
          ...(orderStatus <= 42 && {
            orderDetails: {
              clientId: values.clientId,
              ...(values.subDivision && {
                subDivisionId: values.subDivision,
              }),
              ...(values.project && {
                projectId: values.project,
              }),
              requesterId: values.requesterId,
              isContactInspection: values.isContactInspection,
              inspectionScheduleType: values.inspectionScheduleType,
              photoCount: values.photoCount,
              option1: values.option1,
              option2: values.option2,
              option3: values.option3,
              startDate: values.startDate,
              ...(values.appointmentDate && {
                appointmentDate: values.appointmentDate,
              }),
              ...(values.inspectorDueDate && {
                inspectorDueDate: values.inspectorDueDate,
              }),
              clientDueDate: values.clientDueDate,
              isRushInspection: values.isRushInspection,
              ...(values.rushFeeLabel && {
                rushFeeId: processesProcedure?.rushFees?.find(
                  (rush: any) => rush.label === values.rushFeeLabel
                )?.id,
              }),
              contacts: {
                includeRequester: values.contacts.includeRequester,
                ...(values.contacts.contactIds &&
                  values.contacts.contactIds.length > 0 && {
                    contactIds: values.contacts.contactIds,
                  }),
                ...(values.contacts.externalContacts &&
                  values.contacts.externalContacts.length > 0 && {
                    externalContacts: values.contacts.externalContacts.filter(
                      (contact) =>
                        contact.fullName.trim() !== "" &&
                        contact.email.trim() !== ""
                    ),
                  }),
              },
              notes: values.notes || [],
              attachments: values.attachments || [],
              newAttachments: values.newAttachments || [],
              propertyRecord: finalPropertyRecord,
            },
          }),
          fees: values.fees || [],
          ...(formIdValue && { formId: formIdValue }),
        });

        const requestPayload =
          orderStatus <= 42 && payload && (payload as any).orderDetails
            ? {
                ...payload,
                orderDetails: {
                  ...(payload as any).orderDetails,
                  propertyContacts: propertyContactsPayload,
                },
              }
            : payload;

        const response = await updateOrderRequest(orderId, requestPayload);

        if (
          (response && response.status === 200) ||
          (response && response.status === 201) ||
          (response && response.status === 204)
        ) {
          setSuccessMessage("Order updated successfully!");
          setShowSuccessNotification(true);
          onSaveComplete();
        } else {
          const errorMsg =
            response?.data?.detail ||
            "Failed to update order. Please try again.";
          setErrorMessage(errorMsg);
          setShowErrorNotification(true);
        }
      } catch (error) {
        setErrorMessage("An unexpected error occurred. Please try again.");
        setShowErrorNotification(true);
        throw error;
      } finally {
        setShowLoading(false);
      }
    },
  });

  useEffect(() => {
    dispatch(getOrderDetailsRequest({ id: orderId }));
  }, [orderId]); // eslint-disable-line

  useEffect(() => {
    if (shouldSaveForm) {
      handleFormSubmit();
    }
  }, [shouldSaveForm]); // eslint-disable-line

  useEffect(() => {
    if (!formik.values.isContactInspection) {
      formik.setFieldValue("inspectionScheduleType", "Unannounced");
    }
  }, [formik.values.isContactInspection]); // eslint-disable-line

  useEffect(() => {
    if (orderDetails) {
      const propertyId =
        orderDetails?.propertyRecord?.id || orderDetails?.propertyId || "";

      let optionalPropertyDetails: any[] = [];
      if (orderDetails?.propertyRecord) {
        optionalPropertyDetails = propertyDetailsTypes
          .filter((type) => type.value in orderDetails?.propertyRecord)
          .map((type) => ({
            type: type.value,
            description: orderDetails?.propertyRecord[type.value],
          }))
          .filter(
            (item) => item.description !== null && item.description !== ""
          );
      }

      formik.setValues({
        clientId: orderDetails?.client?.id || "",
        subDivision: orderDetails?.subDivision?.id || "",
        project: orderDetails?.project?.id || "",
        requesterId: orderDetails?.requester?.id || "",
        isContactInspection: orderDetails?.isContactInspection ?? true,
        inspectionScheduleType:
          orderDetails?.inspectionScheduleType || "Appointment",
        photoCount: orderDetails?.photoCount || 0,
        option1: orderDetails?.option1 || "",
        option2: orderDetails?.option2 || "",
        option3: orderDetails?.option3 || "",
        propertyId: propertyId,
        propertyRecord: {
          loanNumber: orderDetails?.propertyRecord?.loanNumber || "",
          // accountNumber: orderDetails?.propertyRecord?.accountNumber || "",
          // referenceNumber: orderDetails?.propertyRecord?.referenceNumber || "",
          propertyName: orderDetails?.propertyRecord?.propertyName || "",
          // collateralOrPropertyType:
          //   orderDetails?.propertyRecord?.collateralOrPropertyType || "",
          // collateralOrPropertyDescription:
          //   orderDetails?.propertyRecord?.collateralOrPropertyDescription || "",
          // yearBuilt: orderDetails?.propertyRecord?.yearBuilt || "",
          // yearRenovated: orderDetails?.propertyRecord?.yearRenovated || "",
          // nameOfBusiness: orderDetails?.propertyRecord?.nameOfBusiness || "",
          // businessPhone: orderDetails?.propertyRecord?.businessPhone || "",
          country: orderDetails?.propertyRecord?.country || "",
          street: orderDetails?.propertyRecord?.street || "",
          address2: orderDetails?.propertyRecord?.address2 || "",
          city: orderDetails?.propertyRecord?.city || "",
          state: orderDetails?.propertyRecord?.state || "",
          zip: orderDetails?.propertyRecord?.zip || "",
          // numberOfUnits: orderDetails?.propertyRecord?.numberOfUnits || "",
          // numberOfBuildings:
          //   orderDetails?.propertyRecord?.numberOfBuildings || "",
          // investorNumber: orderDetails?.propertyRecord?.investorNumber || "",
          // squareFootage: orderDetails?.propertyRecord?.squareFootage || "",
          // operationsAndMaintenanceRequired:
          //   orderDetails?.propertyRecord?.operationsAndMaintenanceRequired ||
          //   false,
          // loanBalance: orderDetails?.propertyRecord?.loanBalance || "",
          // loanBalanceAsOf: orderDetails?.propertyRecord?.loanBalanceAsOf || "",
          optionalPropertyDetails: optionalPropertyDetails,
        },
        propertyContacts: mapPropertyContactsForFormValues(
          orderDetails?.propertyContacts
        ),
        formId: orderDetails?.form?.id || "",
        startDateToday: false,
        startDate: orderDetails?.startDate || "",
        appointmentDate: orderDetails?.appointmentDate || "",
        inspectorDueDate: orderDetails?.inspectorDueDate || "",
        clientDueDate: orderDetails?.clientDueDate || "",
        isRushInspection: !!orderDetails?.rushFeeLabel || false,
        rushFeeLabel: orderDetails?.rushFeeLabel || "",
        contacts: {
          showVisionUsers:
            orderDetails?.contact?.internalContacts
              ?.filter((c: any) => !c.isRequestor)
              .map((c: any) => c.userId)?.length > 0,
          showExtraContacts:
            orderDetails?.contact?.externalContacts?.length > 0,
          includeRequester:
            orderDetails?.contact?.internalContacts?.some(
              (c: any) => c.isRequestor
            ) || false,
          contactIds:
            orderDetails?.contact?.internalContacts
              ?.filter((c: any) => !c.isRequestor)
              .map((c: any) => c.userId) || [],
          externalContacts: (orderDetails?.contact?.externalContacts?.length >
            0 &&
            orderDetails?.contact?.externalContacts?.map((c: any) => ({
              fullName: c.fullName || "",
              email: c.email || "",
            }))) || [{ fullName: "", email: "" }],
        },
        fees: orderDetails?.fees || [],
        notes: orderDetails?.notes || [],
        attachments: orderDetails?.attachments || [],
        newAttachments: [],
      });
    }
  }, [orderDetails]); // eslint-disable-line

  useEffect(() => {
    dispatch(getClientsListRequest());
    dispatch(getAdjustmentTypesRequest());
  }, []); // eslint-disable-line

  useEffect(() => {
    const selectedClient = formik.values.clientId;
    if (selectedClient && selectedClient.toString().trim() !== "") {
      dispatch(getClientSubDivisionListRequest({ id: selectedClient }));
      dispatch(getProjectsRequest({ clientId: selectedClient }));
      dispatch(getClientAllUsersRequest({ id: selectedClient }));
      dispatch(getFormListByClientIdRequest({ clientId: selectedClient }));
      dispatch(getProcessesProcedureRequest({ id: selectedClient }));
    }
  }, [formik.values.clientId]); // eslint-disable-line

  useEffect(() => {
    const selectedForm = formik.values.formId;
    if (selectedForm && selectedForm.toString().trim() !== "") {
      dispatch(getFormDetailsRequest({ id: selectedForm }));
    }
  }, [formik.values.formId]); // eslint-disable-line

  useEffect(() => {
    if (!formik.values.isContactInspection) {
      formik.setFieldValue("inspectionScheduleType", "Unannounced");
    }
  }, [formik.values.isContactInspection]); // eslint-disable-line

  useEffect(() => {
    if (formik.values.startDateToday) {
      const today = new Date();
      const formattedDate = today.toISOString().split("T")[0];
      formik.setFieldValue("startDate", formattedDate);
    }
  }, [formik.values.startDateToday]); // eslint-disable-line

  useEffect(() => {
    if (formik.values.startDate) {
      formik.validateField("appointmentDate");
      formik.validateField("inspectorDueDate");
      formik.validateField("clientDueDate");
    }
  }, [formik.values.startDate]); // eslint-disable-line

  useEffect(() => {
    if (formik.values.clientId && formik.values.clientId !== "") {
      formik.setFieldValue("subDivision", "");
      formik.setFieldValue("project", "");
      formik.setFieldValue("requesterId", "");
      formik.setFieldValue("propertyId", "");
      formik.setFieldValue("formId", "");
      formik.setFieldValue("contacts", {
        showVisionUsers: false,
        showExtraContacts: false,
        includeRequester: false,
        contactIds: [],
        externalContacts: [{ fullName: "", email: "" }],
      });
      formik.setFieldValue("fees", []);
      formik.setFieldValue("notes", []);
      formik.setFieldValue("attachments", []);
      formik.setFieldValue("newAttachments", []);
      formik.setFieldValue("propertyRecord", {
        loanNumber: "",
        accountNumber: "",
        referenceNumber: "",
        propertyName: "",
        collateralOrPropertyType: "",
        collateralOrPropertyDescription: "",
        yearBuilt: "",
        yearRenovated: "",
        nameOfBusiness: "",
        businessPhone: "",
        country: "",
        street: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
        numberOfUnits: "",
        numberOfBuildings: "",
        investorNumber: "",
        squareFootage: "",
        operationsAndMaintenanceRequired: false,
        loanBalance: "",
        loanBalanceAsOf: "",
        optionalPropertyDetails: [],
      });
      formik.setFieldValue("propertyContacts", []);
      dispatch(resetPropertyData());
    }
  }, [formik.values.clientId]); // eslint-disable-line

  // When entering "change property" mode, clear selection so PropertySection shows search UI (like add-order)
  useEffect(() => {
    if (changePropertyMode) {
      formik.setFieldValue("propertyId", "");
      dispatch(resetPropertyData());
    }
  }, [changePropertyMode]); // eslint-disable-line

  // When user selects a property in change-property flow, apply details to formik and exit
  useEffect(() => {
    if (
      !changePropertyMode ||
      !propertyDetails ||
      propertyDetails.id !== formik.values.propertyId
    ) {
      return;
    }
    const d = propertyDetails as any;
    let optionalPropertyDetails: any[] = [];
    if (d) {
      optionalPropertyDetails = propertyDetailsTypes
        .filter((type) => type.value in d)
        .map((type) => ({
          type: type.value,
          description: d[type.value],
        }))
        .filter((item) => item.description !== null && item.description !== "");
    }
    formik.setFieldValue("propertyRecord", {
      loanNumber: d.loanNumber ?? "",
      accountNumber: d.accountNumber ?? "",
      referenceNumber: d.referenceNumber ?? "",
      propertyName: d.propertyName ?? "",
      collateralOrPropertyType: d.collateralOrPropertyType ?? "",
      collateralOrPropertyDescription: d.collateralOrPropertyDescription ?? "",
      yearBuilt: d.yearBuilt ?? "",
      yearRenovated: d.yearRenovated ?? "",
      nameOfBusiness: d.nameOfBusiness ?? "",
      businessPhone: d.businessPhone ?? "",
      country: d.country ?? "",
      street: d.street ?? "",
      address2: d.address2 ?? "",
      city: d.city ?? "",
      state: d.state ?? "",
      zip: d.zip ?? "",
      numberOfUnits: d.numberOfUnits ?? "",
      numberOfBuildings: d.numberOfBuildings ?? "",
      investorNumber: d.investorNumber ?? "",
      squareFootage: d.squareFootage ?? "",
      operationsAndMaintenanceRequired:
        d.operationsAndMaintenanceRequired ?? false,
      loanBalance: d.loanBalance ?? "",
      loanBalanceAsOf: d.loanBalanceAsOf ?? "",
      optionalPropertyDetails: optionalPropertyDetails,
    });
    formik.setFieldValue(
      "propertyContacts",
      mapPropertyContactsForFormValues(d.contacts)
    );
    dispatch(resetPropertyData());
    onExitChangeProperty?.();
  }, [changePropertyMode, propertyDetails, formik.values.propertyId]); // eslint-disable-line

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <form onSubmit={formik.handleSubmit}>
          <Card
            className="card-layout"
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: "#FAFAFA",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
            }}
          >
            <Box className="card-header" sx={{ mb: 2 }}>
              <Typography variant="h5">Order Details</Typography>
            </Box>
            <Grid container spacing={2} sx={{ mb: 2, width: "100%" }}>
              <Grid size={{ xs: 12, sm: subDivisionList?.length ? 6 : 12 }}>
                <FormControl
                  fullWidth
                  size="small"
                  error={
                    formik.touched.clientId && Boolean(formik.errors.clientId)
                  }
                >
                  <InputLabel id="client-label">Client *</InputLabel>
                  <Select
                    labelId="client-label"
                    id="clientId"
                    name="clientId"
                    value={formik.values.clientId}
                    onChange={formik.handleChange}
                    label="Client *"
                    disabled
                  >
                    {clientsList?.map((client: any) => (
                      <MenuItem key={client.id} value={client.id}>
                        {client.clientName}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.clientId && formik.errors.clientId && (
                    <Typography
                      color="error"
                      variant="caption"
                      sx={{ mt: 0.5 }}
                    >
                      {formik.errors.clientId}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              {subDivisionList && subDivisionList.length > 0 && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="sub-division-label">
                      Sub Division *
                    </InputLabel>
                    <Select
                      labelId="sub-division-label"
                      id="subDivision"
                      name="subDivision"
                      value={formik.values.subDivision}
                      onChange={formik.handleChange}
                      label="Sub Division *"
                      disabled={orderStatus > 42}
                    >
                      {subDivisionList?.map((subDivision: any) => (
                        <MenuItem key={subDivision.id} value={subDivision.id}>
                          {subDivision.displayName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
            <Grid container spacing={2} sx={{ mb: 2, width: "100%" }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <ProjectSection
                  formik={formik}
                  projects={projects}
                  setShowErrorNotification={setShowErrorNotification}
                  setErrorMessage={setErrorMessage}
                  setShowLoading={setShowLoading}
                  setShowSuccessNotification={setShowSuccessNotification}
                  setSuccessMessage={setSuccessMessage}
                  disabled={orderStatus > 42}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl
                  fullWidth
                  size="small"
                  error={
                    formik.touched.requesterId &&
                    Boolean(formik.errors.requesterId)
                  }
                >
                  <InputLabel id="requestor-label">Requestor *</InputLabel>
                  <Select
                    labelId="requestor-label"
                    id="requesterId"
                    name="requesterId"
                    value={formik.values.requesterId}
                    onChange={formik.handleChange}
                    label="Requestor *"
                    disabled={orderStatus > 42}
                  >
                    {clientAllUsers?.map((user: any) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.fullName}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.requesterId && formik.errors.requesterId && (
                    <Typography
                      color="error"
                      variant="caption"
                      sx={{ mt: 0.5 }}
                    >
                      {formik.errors.requesterId}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mb: 2, width: "100%" }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="inspection-type-label">
                    Inspection Type *
                  </InputLabel>
                  <Select
                    labelId="inspection-type-label"
                    id="isContactInspection"
                    name="isContactInspection"
                    value={String(formik.values.isContactInspection)}
                    onChange={(e) => {
                      formik.setFieldValue(
                        "isContactInspection",
                        e.target.value === "true"
                      );
                    }}
                    label="Inspection Type *"
                    disabled={orderStatus > 42}
                  >
                    <MenuItem value="true">Contact</MenuItem>
                    <MenuItem value="false">No Contact</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl
                  fullWidth
                  size="small"
                  error={
                    formik.touched.inspectionScheduleType &&
                    Boolean(formik.errors.inspectionScheduleType)
                  }
                >
                  <InputLabel id="schedule-type-label">
                    Appointment or Unannounced?
                  </InputLabel>
                  <Select
                    labelId="schedule-type-label"
                    id="inspectionScheduleType"
                    name="inspectionScheduleType"
                    value={formik.values.inspectionScheduleType}
                    onChange={formik.handleChange}
                    label="Appointment or Unannounced?"
                    disabled={orderStatus > 42}
                  >
                    <MenuItem value="Appointment">Appointment</MenuItem>
                    <MenuItem value="Unannounced">Unannounced</MenuItem>
                  </Select>
                  {formik.touched.inspectionScheduleType &&
                    formik.errors.inspectionScheduleType && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 0.5 }}
                      >
                        {formik.errors.inspectionScheduleType}
                      </Typography>
                    )}
                </FormControl>
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mb: 2, width: "100%" }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InputNumber
                  id="photoCount"
                  name="photoCount"
                  label="Photo Count *"
                  size="small"
                  fullWidth
                  variant="outlined"
                  placeholder="Enter a number"
                  value={formik.values.photoCount}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.photoCount &&
                    Boolean(formik.errors.photoCount)
                  }
                  helperText={
                    formik.touched.photoCount && formik.errors.photoCount
                  }
                  allowDecimal={false}
                  disabled={orderStatus > 42}
                />
              </Grid>
            </Grid>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 500, mb: 1.5, color: "text.primary" }}
              >
                Who should we contact about this order?
              </Typography>
              <Contacts
                formik={formik}
                clientAllUsers={clientAllUsers}
                disabled={orderStatus > 42}
              />
            </Box>
          </Card>
          <Card
            className="card-layout"
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: "#FAFAFA",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
              overflow: changePropertyMode ? "visible" : undefined,
            }}
          >
            <Box className="card-header" sx={{ mb: 1 }}>
              <Typography variant="h5">Property</Typography>
            </Box>
            {changePropertyMode ? (
              <PropertySection
                propertyId={formik.values.propertyId}
                onPropertyChange={(value) =>
                  formik.setFieldValue("propertyId", value)
                }
                clientId={formik.values.clientId}
                error={formik.errors.propertyId as string | undefined}
                touched={formik.touched.propertyId}
              />
            ) : (
              <EditPropertyDetails
                mode="edit"
                formik={formik}
                loading={showLoading}
                clientId={formik.values.clientId}
                disabled={orderStatus > 42}
                simplifiedView
              />
            )}
          </Card>
          <Card
            className="card-layout"
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: "#FAFAFA",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
            }}
          >
            <Box className="card-header" sx={{ mb: 2 }}>
              <Typography variant="h5">Form</Typography>
            </Box>
            <FormControl
              fullWidth
              size="small"
              error={formik.touched.formId && Boolean(formik.errors.formId)}
            >
              <InputLabel id="form-label">Form to Use</InputLabel>
              <Select
                labelId="form-label"
                id="formId"
                name="formId"
                value={formik.values.formId}
                onChange={formik.handleChange}
                label="Form to Use"
                disabled={orderStatus > 34}
              >
                {formsList?.map((form: any) => (
                  <MenuItem key={form?.id} value={form?.id}>
                    {form?.displayName}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.formId && formik.errors.formId && (
                <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                  {formik.errors.formId}
                </Typography>
              )}
            </FormControl>
          </Card>
          <Card
            className="card-layout"
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: "#FAFAFA",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
            }}
          >
            <Box className="card-header" sx={{ mb: 2 }}>
              <Typography variant="h5">Dates</Typography>
            </Box>
            <Grid container spacing={2} sx={{ width: "100%" }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  label="Start Date"
                  value={
                    formik.values.startDate
                      ? dayjs(formik.values.startDate)
                      : null
                  }
                  onChange={(date) =>
                    formik.setFieldValue(
                      "startDate",
                      date?.format("YYYY-MM-DD")
                    )
                  }
                  format="MMMM D, YYYY"
                  disabled={formik.values.startDateToday || orderStatus > 42}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      variant: "outlined",
                      placeholder: "Select Date",
                      error:
                        formik.touched.startDate &&
                        Boolean(formik.errors.startDate),
                      helperText:
                        formik.touched.startDate && formik.errors.startDate,
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  label="Appointment Date/Estimated Completion Date"
                  value={
                    formik.values.appointmentDate
                      ? dayjs(formik.values.appointmentDate)
                      : null
                  }
                  onChange={(date) => {
                    formik.setFieldValue(
                      "appointmentDate",
                      date?.format("YYYY-MM-DD")
                    );
                    formik.setFieldTouched("appointmentDate", true);
                    formik.validateField("appointmentDate");
                  }}
                  format="MMMM D, YYYY"
                  minDate={
                    formik.values.startDate
                      ? dayjs(formik.values.startDate)
                      : dayjs()
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      variant: "outlined",
                      placeholder: "Select Date",
                      error:
                        formik.touched.appointmentDate &&
                        Boolean(formik.errors.appointmentDate),
                      helperText:
                        formik.touched.appointmentDate &&
                        formik.errors.appointmentDate,
                    },
                  }}
                  disabled={orderStatus > 42}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  label="Inspector Due Date"
                  value={
                    formik.values.inspectorDueDate
                      ? dayjs(formik.values.inspectorDueDate)
                      : null
                  }
                  onChange={(date) => {
                    formik.setFieldValue(
                      "inspectorDueDate",
                      date?.format("YYYY-MM-DD")
                    );
                    formik.setFieldTouched("inspectorDueDate", true);
                    formik.validateField("inspectorDueDate");
                  }}
                  format="MMMM D, YYYY"
                  minDate={
                    formik.values.startDate
                      ? dayjs(formik.values.startDate)
                      : dayjs()
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      variant: "outlined",
                      placeholder: "Select Date",
                      error:
                        formik.touched.inspectorDueDate &&
                        Boolean(formik.errors.inspectorDueDate),
                      helperText:
                        formik.touched.inspectorDueDate &&
                        formik.errors.inspectorDueDate,
                    },
                  }}
                  disabled={orderStatus > 42}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  label="Client Due Date"
                  value={
                    formik.values.clientDueDate
                      ? dayjs(formik.values.clientDueDate)
                      : null
                  }
                  onChange={(date) => {
                    const dateValue = date?.format("YYYY-MM-DD");
                    formik.setFieldValue("clientDueDate", dateValue);
                    formik.setFieldTouched("clientDueDate", true);
                    setTimeout(() => formik.validateField("clientDueDate"), 0);
                  }}
                  format="MMMM D, YYYY"
                  minDate={
                    formik.values.startDate
                      ? dayjs(formik.values.startDate)
                      : dayjs()
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      variant: "outlined",
                      placeholder: "Select Date",
                      error:
                        formik.touched.clientDueDate &&
                        Boolean(formik.errors.clientDueDate),
                      helperText:
                        formik.touched.clientDueDate &&
                        formik.errors.clientDueDate,
                    },
                  }}
                  disabled={orderStatus > 42}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl
                  fullWidth
                  size="small"
                  error={
                    formik.touched.rushFeeLabel &&
                    Boolean(formik.errors.rushFeeLabel)
                  }
                >
                  <InputLabel id="rush-type-label">Rush Inspection?</InputLabel>
                  <Select
                    labelId="rush-type-label"
                    id="rushFeeLabel"
                    name="rushFeeLabel"
                    value={formik.values.rushFeeLabel || ""}
                    onChange={(e) => {
                      const val = e.target.value as string;
                      formik.setFieldValue("rushFeeLabel", val);
                      formik.setFieldValue("isRushInspection", !!val);
                      formik.setFieldTouched("rushFeeLabel", true);
                    }}
                    label="Rush Inspection?"
                    disabled={orderStatus > 42}
                  >
                    <MenuItem value="">No Rush</MenuItem>
                    {processesProcedure?.rushFees?.map((rush: any) => (
                      <MenuItem key={rush?.id} value={rush?.label}>
                        {rush?.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.rushFeeLabel &&
                    formik.errors.rushFeeLabel && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 0.5 }}
                      >
                        {formik.errors.rushFeeLabel}
                      </Typography>
                    )}
                </FormControl>
              </Grid>
            </Grid>
          </Card>
          <Card
            className="card-layout"
            ref={feesSectionRef}
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: "#FAFAFA",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
            }}
          >
            <Fees
              data={processesProcedure?.rushFees || []}
              formik={formik}
              formDetails={formDetails}
              adjustmentTypes={adjustmentTypes}
            />
          </Card>
          <Card
            className="card-layout"
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: "#FAFAFA",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
            }}
          >
            <Attachments formik={formik} disabled={orderStatus > 42} />
          </Card>
          <Card
            className="card-layout"
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: "#FAFAFA",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
            }}
          >
            <OptionalOrderDetails formik={formik} disabled={orderStatus > 42} />
          </Card>
          <Card
            className="card-layout"
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: "#FAFAFA",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
            }}
          >
            <AdditionalInformation
              formik={formik}
              disabled={orderStatus > 42}
            />
          </Card>
        </form>
        {(loading || showLoading || projectsLoading) && <Loading />}
        <ErrorNotification
          show={showErrorNotification}
          message={errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
        <Notification
          show={showSuccessNotification}
          message={successMessage}
          onClose={() => setShowSuccessNotification(false)}
        />
      </LocalizationProvider>
    </>
  );
}
